import React from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  Platform,
} from 'react-native';
import ToastMessage from 'react-native-toast-message';
import { ResidentDetailsModalProps } from './types';
import { resolveEventAttendanceFlags } from '@/utils/admin/eventsAttendance';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { onColor } from '@/constants/shared/theme/onColor';
import { GlassCard, SectionHeader } from '@/components/shared/redesign';
import { SVG_COMPONENTS } from '@/assets/admin';
import { toastConfig } from '@/components/common/ToastConfig';

export const ResidentDetailsModal: React.FC<ResidentDetailsModalProps> = ({
  visible,
  resident,
  onClose,
  onCheckIn,
  eventId,
}) => {
  const { redesign, semantic } = useAdminColors();
  if (!resident) return null;

  const { checkedIn: isCheckedIn, hasRsvp } = resolveEventAttendanceFlags(
    resident,
    eventId,
  );

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <SafeAreaView style={[styles.container, { backgroundColor: redesign.bg.base }]}>
        <View style={[styles.header, { borderBottomColor: redesign.card.border }]}>
          <Text style={[styles.title, { color: redesign.text.primary }]}>
            Resident Details
          </Text>
          <TouchableOpacity
            style={[styles.closeButton, { backgroundColor: redesign.glass.buttonBg }]}
            onPress={onClose}
            accessibilityLabel="Close resident details"
            accessibilityHint="Tap to close the resident details modal"
            accessibilityRole="button"
          >
            <SVG_COMPONENTS.CANCEL_X
              width={14}
              height={14}
              color={redesign.text.primary}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.content}>
          {/* Resident Info */}
          <GlassCard padding={16} borderRadius={12} style={styles.residentInfoCard}>
            <View style={styles.residentInfo}>
              <View
                style={[
                  styles.residentAvatar,
                  { backgroundColor: redesign.accent.blueBgLight },
                ]}
              >
                <Text
                  style={[styles.residentAvatarText, { color: semantic.text.link }]}
                >
                  {getInitials(resident.firstName, resident.lastName)}
                </Text>
              </View>
              <View style={styles.residentDetails}>
                <Text
                  style={[styles.residentName, { color: redesign.text.primary }]}
                >
                  {resident.firstName} {resident.lastName}
                </Text>
                <Text
                  style={[styles.residentEmail, { color: redesign.text.muted }]}
                >
                  {resident.email}
                </Text>
                {resident.unitNumberSpace && (
                  <Text
                    style={[styles.residentUnit, { color: redesign.text.muted }]}
                  >
                    Unit {resident.unitNumberSpace}
                  </Text>
                )}
                <View
                  style={[
                    styles.residentStatus,
                    { backgroundColor: redesign.glass.buttonBg },
                  ]}
                >
                  <Text
                    style={[
                      styles.residentStatusText,
                      { color: redesign.text.subtle },
                    ]}
                  >
                    {resident.statusType?.charAt(0).toUpperCase() +
                      resident.statusType?.slice(1)}{' '}
                    Resident
                  </Text>
                </View>
              </View>
            </View>
          </GlassCard>

          {/* Check-in Status */}
          <View style={styles.checkInSection}>
            <SectionHeader title="Check-in Status" />
            <GlassCard padding={16} borderRadius={12} style={styles.statusCard}>
              {isCheckedIn ? (
                <View style={styles.statusRow}>
                  <View
                    style={[
                      styles.statusDotLarge,
                      { backgroundColor: semantic.status.success },
                    ]}
                  />
                  <Text
                    style={[styles.statusTextLarge, { color: semantic.status.success }]}
                  >
                    Checked In
                  </Text>
                </View>
              ) : (
                <View style={styles.statusRow}>
                  <View
                    style={[
                      styles.statusDotLarge,
                      { backgroundColor: redesign.text.subtle },
                    ]}
                  />
                  <Text
                    style={[styles.statusTextLarge, { color: redesign.text.subtle }]}
                  >
                    Not Checked In
                  </Text>
                </View>
              )}
            </GlassCard>

            {/* RSVP Status */}
            {hasRsvp && (
              <View
                style={[
                  styles.rsvpStatusContainer,
                  { backgroundColor: redesign.accent.blueBgLight },
                ]}
              >
                <SVG_COMPONENTS.CHECK_CIRCLE
                  width={14}
                  height={14}
                  color={semantic.text.link}
                />
                <Text
                  style={[styles.rsvpStatusText, { color: redesign.text.primary }]}
                >
                  RSVP'd to this event
                </Text>
              </View>
            )}

            {/* Check-in Actions */}
            <View style={styles.checkInActions}>
              {!isCheckedIn ? (
                <TouchableOpacity
                  style={[
                    styles.checkInButton,
                    { backgroundColor: semantic.status.success },
                  ]}
                  onPress={() => onCheckIn(resident, true)}
                  accessibilityLabel={`Check in ${resident.firstName} ${resident.lastName}`}
                  accessibilityHint="Tap to check in this resident to the event"
                  accessibilityRole="button"
                >
                  <Text
                    style={[
                      styles.checkInButtonText,
                      { color: onColor(semantic.status.success) },
                    ]}
                  >
                    Check In
                  </Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={[
                    styles.checkOutButton,
                    {
                      backgroundColor: redesign.glass.buttonBg,
                      borderColor: redesign.card.border,
                    },
                  ]}
                  onPress={() => onCheckIn(resident, false)}
                  accessibilityLabel={`Check out ${resident.firstName} ${resident.lastName}`}
                  accessibilityHint="Tap to check out this resident from the event"
                  accessibilityRole="button"
                >
                  <Text
                    style={[styles.checkOutButtonText, { color: redesign.text.primary }]}
                  >
                    Check Out
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </SafeAreaView>
      {Platform.OS !== 'web' && (
        <ToastMessage config={toastConfig} topOffset={60} />
      )}
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  residentInfoCard: {
    marginBottom: 24,
  },
  residentInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  residentAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  residentAvatarText: {
    fontSize: 24,
    fontWeight: '700',
  },
  residentDetails: {
    flex: 1,
  },
  residentName: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 4,
  },
  residentEmail: {
    fontSize: 16,
    marginBottom: 4,
  },
  residentUnit: {
    fontSize: 14,
    marginBottom: 8,
  },
  residentStatus: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  residentStatusText: {
    fontSize: 12,
    fontWeight: '500',
  },
  checkInSection: {
    flex: 1,
  },
  statusCard: {
    marginBottom: 16,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusDotLarge: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  statusTextLarge: {
    fontSize: 16,
    fontWeight: '600',
  },
  rsvpStatusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 24,
    alignSelf: 'flex-start',
  },
  rsvpStatusText: {
    fontSize: 14,
    fontWeight: '500',
  },
  checkInActions: {
    marginTop: 'auto',
    paddingTop: 24,
  },
  checkInButton: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  checkInButtonText: {
    // Foreground applied inline via onColor(status.success): success is a LIGHT
    // green in the dark theme, where a white label reads at ~2:1.
    fontSize: 16,
    fontWeight: '600',
  },
  checkOutButton: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
  },
  checkOutButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});
